import { BehaviorSubject, Observable } from 'rxjs';
import { ConfigSystemParams, StorageValues } from 'src/app/core/consts';
import { ConfigSystemService } from 'src/app/core/services/utelize/system/config-system/config-system-service';
import { DecryptAesClientSide, EncryptAesClientSide } from 'src/app/core/encryption';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { Injectable } from '@angular/core';
import { SystemUserDTO } from 'src/app/core/models/system/user/system-user.dto';

export interface ClockZoneDefinition {
  flagAlt?   : string;
  flagIcon?  : string;
  flagImg?   : string;
  key        : string;
  timezone   : string;
  tipSub     : string;
  getLabel   : () => string;
  getTipTitle: () => string;
}

export interface ClockZoneTick {
  iconTime: string;
  label   : string;
  time    : string;
}

@Injectable({ providedIn: 'root' })
export class ClockService {

  private bar$         : BehaviorSubject<{ [key: string]: ClockZoneTick }>=new BehaviorSubject<{ [key: string]: ClockZoneTick }>( {} );
  private barDismissed$: BehaviorSubject<boolean                         >=new BehaviorSubject<boolean                         >(true);
  private gmtBstLabel  : string='GMT';
  private intervalId   : any;
  private localTimezone=Intl.DateTimeFormat().resolvedOptions().timeZone;
  private startedFor   : number | undefined;
  private zones        : Array<ClockZoneDefinition>;

  constructor(
    private configSystemService: ConfigSystemService,
    private exceptionsService  : ExceptionsService
  ) {
    this.zones=this.buildZones();
  }

  public get barDismissedChanges$(): Observable<boolean> {
    try {
      return this.barDismissed$.asObservable();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return this.barDismissed$.asObservable();
    }
  }

  public get localTimezoneLabel(): string {
    try {
      return this.localTimezone;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return '';
    }
  }

  public get ticks$(): Observable<{ [key: string]: ClockZoneTick }> {
    try {
      return this.bar$.asObservable();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return this.bar$.asObservable();
    }
  }

  public get zoneDefinitions(): ReadonlyArray<ClockZoneDefinition> {
    try {
      return this.zones;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return [];
    }
  }

  public ensureStarted(curUser: SystemUserDTO | undefined): void {
    try {
      const userId=curUser?.userId ?? -1;

      if (this.startedFor===userId && this.intervalId)
        return;

      this.startedFor=userId;
      this.tick();

      if (!this.intervalId)
        this.intervalId=setInterval(() => { this.tick(); }, 1000);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public loadBarDismissed(curUser: SystemUserDTO | undefined): boolean {
    try {
      const raw      =DecryptAesClientSide(localStorage.getItem(this.barDismissedKey(curUser)));
      const dismissed=(raw==='false') ? false : true;

      this.barDismissed$.next(dismissed);
      return dismissed;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return true;
    }
  }

  public loadSelectedZoneKeys(curUser: SystemUserDTO | undefined): { [key: string]: boolean } {
    try {
      const fallback: { [key: string]: boolean }={ local: true };
      const raw                                 =DecryptAesClientSide(localStorage.getItem(this.selectedZonesKey(curUser)));

      if (!raw)
        return fallback;

      const keys=<string[]>JSON.parse(raw);

      if (!Array.isArray(keys) || keys.length===0)
        return fallback;

      const validKeys=new Set(this.zones.map(z => z.key));
      const next     : { [key: string]: boolean }={};

      keys.forEach(k => {
        if (validKeys.has(k))
          next[k]=true;
      });

      return Object.keys(next).length>0 ? next : fallback;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return { local: true };
    }
  }

  public setBarDismissed(curUser: SystemUserDTO | undefined, dismissed: boolean): void {
    try {
      localStorage.setItem(this.barDismissedKey(curUser), EncryptAesClientSide(String(dismissed)));
      this.barDismissed$.next(dismissed);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public saveSelectedZoneKeys(curUser: SystemUserDTO | undefined, checks: { [key: string]: boolean }): void {
    try {
      const keys=this.zones.filter(z => checks[z.key]).map(z => z.key);

      localStorage.setItem(this.selectedZonesKey(curUser), EncryptAesClientSide(JSON.stringify(keys)));
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public stop(): void {
    try {
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId=undefined;
      }

      this.startedFor=undefined;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public timezoneOffsetMinutes(timezone: string): number {
    try {
      const parts =new Intl.DateTimeFormat('en-GB', { timeZone: timezone, timeZoneName: 'longOffset' }).formatToParts(new Date());
      const offset=parts.find(p => p.type==='timeZoneName')?.value ?? 'GMT';
      const match =offset.match(/GMT([+-])(\d{1,2})(?::?(\d{2}))?/);

      if (!match)
        return 0;

      const sign   =match[1]==='-' ? -1 : 1;
      const hours  =parseInt(match[2], 10);
      const minutes=match[3] ? parseInt(match[3], 10) : 0;

      return sign*(hours*60+minutes);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return 0;
    }
  }

  private barDismissedKey(curUser: SystemUserDTO | undefined): string {
    try {
      return `${StorageValues.encClockBarDismissed_}${curUser?.userId ?? -1}`;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return `${StorageValues.encClockBarDismissed_}-1`;
    }
  }

  private buildZones(): Array<ClockZoneDefinition> {
    try {
      return [
        { key: 'local' , timezone: this.localTimezone   , flagIcon: 'bi bi-house-door-fill'                                     , getLabel: ()=>'LOCAL'         , getTipTitle: ()=>'LOCAL - Your timezone'                                                                                       , tipSub: ''                                    },
        { key: 'pt'    , timezone: 'America/Los_Angeles', flagImg : 'assets/images/flags/us.svg', flagAlt: 'United States flag' , getLabel: ()=>'PT'            , getTipTitle: ()=>'PT - Pacific Time'                                                                                           , tipSub: 'Los Angeles, San Francisco, Seattle' },
        { key: 'et'    , timezone: 'America/New_York'   , flagImg : 'assets/images/flags/us.svg', flagAlt: 'United States flag' , getLabel: ()=>'ET'            , getTipTitle: ()=>'ET - Eastern Time'                                                                                           , tipSub: 'New York, Boston, Miami'             },
        { key: 'utc'   , timezone: 'UTC'                , flagIcon: 'bi bi-globe'                                               , getLabel: ()=>'UTC'           , getTipTitle: ()=>'UTC - Coordinated Universal Time'                                                                            , tipSub: 'Greenwich Mean Time reference'       },
        { key: 'gmtbst', timezone: 'Europe/London'      , flagImg : 'assets/images/flags/gb.svg', flagAlt: 'United Kingdom flag', getLabel: ()=>this.gmtBstLabel, getTipTitle: ()=>`${this.gmtBstLabel} - ${this.gmtBstLabel==='BST' ? 'British Summer Time' : 'Greenwich Mean Time'}`           , tipSub: 'London, Manchester, Edinburgh'       },
        { key: 'cet'   , timezone: 'Europe/Paris'       , flagImg : 'assets/images/flags/de.svg', flagAlt: 'Germany flag'       , getLabel: ()=>'CET'           , getTipTitle: ()=>'CET - Central European Time'                                                                                 , tipSub: 'Berlin, Paris, Rome, Madrid'         },
        { key: 'gst'   , timezone: 'Asia/Dubai'         , flagImg : 'assets/images/flags/ae.svg', flagAlt: 'UAE flag'           , getLabel: ()=>'GST'           , getTipTitle: ()=>'GST - Gulf Standard Time'                                                                                    , tipSub: 'Dubai, Abu Dhabi, Muscat'            },
        { key: 'ist'   , timezone: 'Asia/Kolkata'       , flagImg : 'assets/images/flags/in.svg', flagAlt: 'India flag'         , getLabel: ()=>'IST'           , getTipTitle: ()=>'IST - India Standard Time'                                                                                   , tipSub: 'Mumbai, Delhi, Bangalore, Chennai'   },
        { key: 'sgt'   , timezone: 'Asia/Singapore'     , flagImg : 'assets/images/flags/sg.svg', flagAlt: 'Singapore flag'     , getLabel: ()=>'SGT'           , getTipTitle: ()=>'SGT - Singapore Standard Time'                                                                               , tipSub: 'Singapore'                           },
        { key: 'cst'   , timezone: 'Asia/Shanghai'      , flagImg : 'assets/images/flags/cn.svg', flagAlt: 'China flag'         , getLabel: ()=>'CST'           , getTipTitle: ()=>'CST - China Standard Time'                                                                                   , tipSub: 'Shanghai, Beijing, Shenzhen'         },
        { key: 'jst'   , timezone: 'Asia/Tokyo'         , flagImg : 'assets/images/flags/jp.svg', flagAlt: 'Japan flag'         , getLabel: ()=>'JST'           , getTipTitle: ()=>'JST - Japan Standard Time'                                                                                   , tipSub: 'Tokyo, Osaka, Kyoto'                 }
      ];
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return [];
    }
  }

  private selectedZonesKey(curUser: SystemUserDTO | undefined): string {
    try {
      return `${StorageValues.encClockIconZones_}${curUser?.userId ?? -1}`;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return `${StorageValues.encClockIconZones_}-1`;
    }
  }

  private tick(): void {
    try {
      const now             =new Date();
      const showSeconds     =this.configSystemService.getValueAsBoolean(ConfigSystemParams.TopBarShowClockSeconds,      false);
      const showSecondsLocal=this.configSystemService.getValueAsBoolean(ConfigSystemParams.TopBarShowClockSecondsLocal, false);
      const opts            : Intl.DateTimeFormatOptions={ hour: '2-digit', hour12: false, minute: '2-digit', ...(showSeconds      ? { second: '2-digit' } : {}) };
      const optsLocal       : Intl.DateTimeFormatOptions={ hour: '2-digit', hour12: false, minute: '2-digit', ...(showSecondsLocal ? { second: '2-digit' } : {}) };
      const optsIcon        : Intl.DateTimeFormatOptions={ hour: '2-digit', hour12: false, minute: '2-digit',    second: '2-digit'                               };

      this.gmtBstLabel=now.toLocaleString('en-GB', { timeZone: 'Europe/London', timeZoneName: 'short' }).includes('BST') ? 'BST' : 'GMT';

      const next: { [key: string]: ClockZoneTick }={};

      this.zones.forEach(zone => {
        const isLocal       =zone.key==='local';
        const displayOptions=isLocal ? optsLocal : opts;

        next[zone.key]={
          iconTime: now.toLocaleTimeString([], { ...optsIcon,       timeZone: zone.timezone }),
          label   : zone.getLabel         (                                                  ),
          time    : now.toLocaleTimeString([], { ...displayOptions, timeZone: zone.timezone })
        };
      });

      this.bar$.next(next);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
}
